// =============================================================================
// SARS modulus-10 check digit (BRS App. B §8.1 / §8.2)
// =============================================================================
//
// Verbatim from SARS's PAYE Employer Reconciliation BRS (V25-3-0, Appendix B,
// §8.1-8.2): a SARS reference is ten digits, the first nine are data and the
// tenth is the check digit. Of the nine data digits, the odd positions
// (1, 3, 5, 7, 9, counting from the left, 1-indexed) are doubled - collapsing
// a doubled result above 9 to a single digit by summing its digits, which is
// equivalent to subtracting 9 (no single digit doubles past 18) - and the
// even positions (2, 4, 6, 8) are added as they are. If the last digit of
// that sum is 0 the check digit is 0; otherwise it is 10 minus that last
// digit.
//
// An income tax reference always runs this arithmetic unmodified (§8.1). A
// PAYE employer reference runs it unmodified ONLY when it begins 0, 1, 2, 3,
// or 9; every other leading digit means SARS's own generator built the
// reference by substituting a leading 4 before the calculation (§8.2), so
// verifying it requires that same substitution. Measured on production, the
// two branches disagree on every stored PAYE reference - a dispatch wired
// backwards rejects every real employer while looking correct in review,
// since both branches are the same arithmetic differing by one digit.
const SARS_REFERENCE_LENGTH = 10;
const PAYE_NO_SUBSTITUTION_LEADING_DIGITS = new Set(['0', '1', '2', '3', '9']);
function assertCanonicalReference(reference) {
    if (!new RegExp(`^\\d{${SARS_REFERENCE_LENGTH}}$`).test(reference)) {
        throw new Error(`SARS check digit requires a canonical ${SARS_REFERENCE_LENGTH}-digit reference, received: ${JSON.stringify(reference)}`);
    }
}
/**
 * The modulus-10 check digit SARS expects for nine data digits, per BRS
 * App. B §8.1/§8.2's shared arithmetic (the two branches differ only in
 * which nine digits are fed in - see `passesSarsCheckDigit`).
 */
function sarsCheckDigit(dataDigits) {
    if (!/^\d{9}$/.test(dataDigits)) {
        throw new Error(`sarsCheckDigit requires exactly 9 data digits, received: ${JSON.stringify(dataDigits)}`);
    }
    let sum = 0;
    for (let position = 0; position < 9; position++) {
        const digit = Number(dataDigits[position]);
        // 0-indexed even positions are the 1-indexed odd positions (1,3,5,7,9).
        if (position % 2 === 0) {
            const doubled = digit * 2;
            sum += doubled > 9 ? doubled - 9 : doubled;
        }
        else {
            sum += digit;
        }
    }
    const lastDigit = sum % 10;
    return lastDigit === 0 ? 0 : 10 - lastDigit;
}
/**
 * Which BRS branch a PAYE reference's leading digit routes to: §8.1
 * (labelled `'INCOME_TAX'` - the same unmodified arithmetic an income tax
 * reference always uses) for a leading 0, 1, 2, 3 or 9; §8.2 (`'PAYE'`, first
 * digit read as 4) for every other leading digit.
 */
function payeCheckDigitAlgorithm(reference) {
    assertCanonicalReference(reference);
    return PAYE_NO_SUBSTITUTION_LEADING_DIGITS.has(reference.slice(0, 1)) ? 'INCOME_TAX' : 'PAYE';
}
/**
 * Whether a canonical 10-digit reference matches its SARS check digit under
 * the given algorithm. `'PAYE'` reads the reference's first digit as `'4'`
 * for the purpose of the calculation (BRS §8.2's substitution); `'INCOME_TAX'`
 * uses the reference unmodified (§8.1).
 */
function passesSarsCheckDigit(reference, algorithm) {
    assertCanonicalReference(reference);
    const effectiveFirstDigit = algorithm === 'PAYE' ? '4' : reference.slice(0, 1);
    const dataDigits = effectiveFirstDigit + reference.slice(1, 9);
    return sarsCheckDigit(dataDigits) === Number(reference[9]);
}
/** An income tax reference always runs the unmodified §8.1 arithmetic. */
export function passesIncomeTaxCheckDigit(reference) {
    return passesSarsCheckDigit(reference, 'INCOME_TAX');
}
/**
 * A PAYE employer reference dispatches to §8.1 or §8.2 by its own leading
 * digit - see `payeCheckDigitAlgorithm`.
 */
export function passesPayeCheckDigit(reference) {
    return passesSarsCheckDigit(reference, payeCheckDigitAlgorithm(reference));
}
/**
 * A VAT number is not covered by the published BRS, so this applies the same
 * unmodified §8.1 arithmetic as an income tax reference - a structural
 * inference from the shared 10-digit SARS reference format, not a SARS
 * statement. Because of that, a VAT check-digit failure is enforced as a
 * warning at the API layer, never a refusal (see `vatCheckDigitWarning`).
 */
export function passesVatCheckDigit(vatNumber) {
    return passesSarsCheckDigit(vatNumber, 'INCOME_TAX');
}
